import React, {useMemo} from 'react';
import {Navigate, Route, Routes} from 'react-router-dom';
import AccountDetails from './pages/AccountDetailsPage/AccountPage';
import {PrecursorWizardPage} from './pages/PrecursorWizard/PrecursorWizard';
import {PrecursorSummaryPage} from './pages/PrecursorSummaryPage/PrecursorSummaryPage';
import Layout from './components/Layout/Layout';
import ClientGoalsPage from './pages/ClientGoalsPage/ClientGoalsPage';
import AccountProvider from './contexts/AccountContext';
import CreateRecommend from './features/recommendations/CreateRecommend/CreateRecommend';
import {CreateBuilder} from './features/builder/CreateBuilder/CreateBuilder';
import {ClientBuilderHandoverPage} from './features/builder/ClientBuilderPage/ClientBuilder';
import CreateTask from './features/tasks/CreateTask/CreateTask';
import {HandoverNotesPage} from './pages/HandoverNotesPage/HandoverNotesPage';
import {HandoverSummaryPage} from './pages/HandoverSummaryPage/HandoverSummaryPage';
import {ReviewContextProvider} from './contexts/ReviewContext';
import HandoverGoalsPage from './pages/ClientGoalsPage/HandoverGoalsPage';
import {HandoverTaskPage} from './pages/TasksPage/Tasks';
import {HandoverIndexRedirectPage} from './pages/HandoverSummaryPage/HandoverIndexRedirectPage';
import {ContactPage} from './components/ContactPage/ContactPage';
import {PrePrecursorForm} from './components/PrecursorForm/PrePrecursor';
import CreateGoal from './features/goals/CreateGoal/CreateGoal';
import {LeadBasicForm} from './pages/LeadsPage/LeadBasic';
import {LeadFormContextProvider} from './pages/LeadsPage/LeadFormContext';
import {LeadAddressForm} from './pages/LeadsPage/LeadAddress';
import {LeadsRecommend} from './pages/LeadsPage/LeadsReccs';
import {LeadSetupAccount} from './pages/LeadsPage/LeadSetupAccount';
import {TestHomePage} from './pages/TestSetupPages/TestHomepage';
import {TestAccountPage} from './pages/TestSetupPages/TestAccountPage';
import {TestReviewPage} from './pages/TestSetupPages/TestReviewPage';
import {
  TestAnyOpenReviewPage,
  TestInstruction,
} from './pages/TestSetupPages/TestAnyOpenReviewPage';
import {getEnvironment} from './utils/envTest';
import {AddAccountContextProvider} from './pages/AddExtAccountPage/AddAccountContext';
import {
  AddAccountBasicForm,
  AddAccountRedirect,
} from './pages/AddExtAccountPage/AddAccountBasic';
import {AddAccountAddressForm} from './pages/AddExtAccountPage/AddExtAccountAddress';
import {AddAccountSetupAccount} from './pages/AddExtAccountPage/AddAccountSetupAccount';
import {
  AddAccountRecommend,
  AddAccountRecommendBuilder,
} from './pages/AddExtAccountPage/AddAccountReccs';
import Dashboard from './pages/DashboardPage/DashboardPage';
import {AddAccountSummaryForm} from './pages/AddExtAccountPage/AddAccountSummary';
import {InvitePage} from './pages/AnnualReviewsPage/InvitePage';
import {InvitedPage} from './pages/AnnualReviewsPage/InvitedPage';
import {ScheduledPage} from './pages/AnnualReviewsPage/ScheduledPage';
import {CompletedPage} from './pages/AnnualReviewsPage/CompletedPage';
import {DeclinedPage} from './pages/AnnualReviewsPage/DeclinedPage';
import {UncontactablePage} from './pages/AnnualReviewsPage/UncontactablePage';
import {TodayPage} from './pages/AnnualReviewsPage/TodayPage';
import {AccountContactSearchPage} from './pages/AccountContactSearchPage/AccountContactSearchPage';
import {ClientRecommendsHandoverPage} from './features/recommendations/ClientRecommendsPage/ClientRecommends';
import SPPDocumentsPage from './pages/SPPDocumentsPage/SPPDocumentsPage';
import BulkAdminPage from './pages/BulkAdminPage/BulkAdminPage';
import {FeatureBoundary} from './contexts/FeatureBoundary';
import {GlobalPermissionEnum} from './schema-types/globalTypes';
import {TestNewTestingReviewPage} from './pages/TestSetupPages/TestSFPAccountSetupPage';
import ReviewsPage from './pages/ReviewsPage/ReviewsPage';
import {AuditPage} from './pages/PermissionsAuditPage/AuditPage';
import {ResilienceStepPage} from './pages/VulnerabilityStepPage/VulnerabilityStepPage';
import {PostcursorWizardPage} from './pages/PostcursorPage/PostcursorWizard';
import {EmailAuditPage} from './pages/EmailAuditPage/EmailAuditPage';
import {StaffSearchPage} from './pages/StaffPage/StaffSearchPage';
import {StaffPage} from './pages/StaffPage/StaffPage';
import {PermissionsAuditProvider} from './pages/PermissionsAuditPage/PermissionsAuditProvider';
import {AdminPage} from './pages/PermissionsAuditPage/PermissionsAuditPage';
import ManageClientsPageWrapped from './pages/ManageStakeholdersPage/ManageClientsPage';
import {ManageGlobalNotifications} from './pages/ManageGlobalNotifications/ManageGlobalNotifications';
import {TestSetGroupPage} from './pages/TestSetupPages/TestGodPage';
import {RestoreButton} from './pages/PermissionsAuditPage/PermissionsDetailPanelContent';
import {handoverStepRoutes} from './pages/HandoverSummaryPage/HandoverStepTypes';
import {ThrowErrorPage} from './pages/ThrowErrorPage';
import {ReportPackPage} from './pages/ReportPackPage/ReportPackPage';
import ReviewRedirect from './pages/ReviewRedirect/ReviewRedirect';
import {AccountRedirect} from './pages/AccountRedirectPage/AccountRedirect';
import {SoftDeletePage} from './pages/PermissionsAuditPage/SoftDeletePage';
import {ReportPackDashboardPage} from './pages/ReportPackDashboardPage/ReportPackDashboardPage';
import {ManageDisclosuresPage} from './pages/ReportPackPage/AddGenericDocumentPage';
import {RiskPage} from './features/risk/RiskPage';
import {RiskNotesPage} from './features/risk-notes/RiskNotesPage';
import {AnnualReviewsDashboardPage} from './pages/AnnualReviewsPage/AnnualReviewsDashboard';
import {QuickTestAccounts, RegressionPage} from './components/Header/QuickTestAccounts';
import {ANNUAL_REVIEWS_NEW} from './constants/Routes';
import {IdentityUserSearch} from './pages/IdentityPage/IndentityUserSearch';
import {SingleIdentityPage} from './pages/IdentityPage/SingleIdentityPage';
import {AddAccountRedirectReplatforming} from './pages/Replatforming/AddAccountRedirectReplatforming';

function AddAccountRecommendRoutes() {
  // root path = /leads/:leadId/recommends/:accountId/:reviewId
  return (
    <Routes>
      <Route index element={<LeadsRecommend />} />
      <Route path="/requirements" index element={<AddAccountRecommend />} />
      <Route path="/recommends-builder" element={<AddAccountRecommendBuilder />} />
      <Route
        path="/builder"
        element={
          <FeatureBoundary
            features={[GlobalPermissionEnum.can_access_dentist_recommendation_builder]}
            showErrorIfDenied
          >
            <CreateBuilder isSppDA />
          </FeatureBoundary>
        }
      />
    </Routes>
  );
}

function LeadRoutes() {
  // root path = /leads/:leadId
  return (
    <FeatureBoundary
      showErrorIfDenied
      features={[GlobalPermissionEnum.can_access_lead_creator]}
    >
      <LeadFormContextProvider>
        <Routes>
          <Route path="/basic" index element={<LeadBasicForm formFields={[]} />} />
          <Route path="/address" element={<LeadAddressForm formFields={[]} />} />
          <Route path="/setup-account" element={<LeadSetupAccount />} />
          {/* LeadSetupAccount goes to account page */}
          {/* <Route
            path="/recommends/:accountId/:reviewId/*"
            element={<LeadRecommendRoutes />}
          /> */}
          {/* <Route index element={<AddLead />} /> */}
        </Routes>
      </LeadFormContextProvider>
    </FeatureBoundary>
  );
}

function AddAccountExistingAccountRoutes() {
  return (
    <AccountProvider>
      <Routes>
        <Route path="/setup-account" element={<AddAccountSetupAccount />} />
        <Route path="/:reviewId/*" element={<AddAccountRecommendRoutes />} />
      </Routes>
    </AccountProvider>
  );
}

function AddAccountRoutes() {
  return (
    <FeatureBoundary
      features={[GlobalPermissionEnum.can_access_client_creator]}
      showErrorIfDenied
    >
      <AddAccountContextProvider>
        <Routes>
          <Route path="/basic" index element={<AddAccountBasicForm formFields={[]} />} />
          <Route path="/address" element={<AddAccountAddressForm formFields={[]} />} />
          <Route path="/summary" element={<AddAccountSummaryForm />} />
          <Route path="/:accountId/*" element={<AddAccountExistingAccountRoutes />} />
        </Routes>
      </AddAccountContextProvider>
    </FeatureBoundary>
  );
}

/**
 * root path = /replatforming/
 * @returns
 */
function AddAccountRoutesReplatforming() {
  return (
    <FeatureBoundary
      features={[GlobalPermissionEnum.can_access_client_creator]}
      showErrorIfDenied
    >
      <Routes>
        <Route
          path="/check"
          index
          element={<div>Domicile and Residency Eligibility Check</div>}
        />
        <Route path="/details/:accountId" element={<div>Client Account Details</div>} />
        <Route path="/summary/:accountId" element={<div>Summary</div>} />
      </Routes>
    </FeatureBoundary>
  );
}

function ReviewRoutes() {
  // Permissions done around ReviewRoutes, not within
  return (
    <ReviewContextProvider>
      <Routes>
        {/* Used for SFP and SPP */}
        <Route
          path={`${handoverStepRoutes.requirements}`}
          element={
            <FeatureBoundary
              features={[GlobalPermissionEnum.can_access_ehons_reqs]}
              showErrorIfDenied
            >
              <ClientRecommendsHandoverPage />
            </FeatureBoundary>
          }
        />
        {/* This one is only for ehons requirements */}
        <Route
          path="/recommends-builder"
          element={
            <FeatureBoundary
              features={[GlobalPermissionEnum.can_access_ehons_notes_goals]}
              showErrorIfDenied
            >
              <CreateRecommend isSppDA={false} />
            </FeatureBoundary>
          }
        />
        <Route
          path="/builder"
          element={
            <FeatureBoundary
              features={[GlobalPermissionEnum.can_access_ehons_notes_goals]}
              showErrorIfDenied
            >
              <FeatureBoundary
                features={[
                  GlobalPermissionEnum.can_access_dentist_recommendation_builder,
                ]}
                showErrorIfDenied
              >
                <CreateBuilder isSppDA={false} />
              </FeatureBoundary>
            </FeatureBoundary>
          }
        />
        <Route
          path={`${handoverStepRoutes.requirements}-builder`}
          element={
            <FeatureBoundary
              features={[GlobalPermissionEnum.can_access_ehons_reqs]}
              showErrorIfDenied
            >
              <ClientBuilderHandoverPage />
            </FeatureBoundary>
          }
        />

        <Route
          path="/header-precursor"
          element={
            <FeatureBoundary
              features={[GlobalPermissionEnum.can_access_ehons_notes_goals]}
              showErrorIfDenied
            >
              <PrecursorWizardPage />
            </FeatureBoundary>
          }
        />
        <Route
          path={`${handoverStepRoutes.meeting}`}
          index
          element={
            <FeatureBoundary
              features={[GlobalPermissionEnum.can_access_ehons_notes_goals]}
              showErrorIfDenied
            >
              <PrecursorSummaryPage />
            </FeatureBoundary>
          }
        />
        <Route
          path={`${handoverStepRoutes.resilience}`}
          element={
            <FeatureBoundary
              features={[GlobalPermissionEnum.can_access_ehons_notes_goals]}
              showErrorIfDenied
            >
              <ResilienceStepPage />
            </FeatureBoundary>
          }
        />
        <Route
          path="/resilience-postcursor"
          element={
            <FeatureBoundary
              features={[GlobalPermissionEnum.can_access_ehons_notes_goals]}
              showErrorIfDenied
            >
              <PostcursorWizardPage />
            </FeatureBoundary>
          }
        />
        <Route
          index
          element={
            <FeatureBoundary
              features={[GlobalPermissionEnum.can_access_ehons_notes_goals]}
              showErrorIfDenied
            >
              <HandoverIndexRedirectPage />
            </FeatureBoundary>
          }
        />
        <Route
          path={`${handoverStepRoutes.notes}`}
          element={
            <FeatureBoundary
              features={[GlobalPermissionEnum.can_access_ehons_notes_goals]}
              showErrorIfDenied
            >
              <HandoverNotesPage />
            </FeatureBoundary>
          }
        />

        <Route
          path={`${handoverStepRoutes.goals}`}
          element={
            <FeatureBoundary
              features={[GlobalPermissionEnum.can_access_ehons_notes_goals]}
              showErrorIfDenied
            >
              <HandoverGoalsPage />
            </FeatureBoundary>
          }
        />
        <Route
          path={`${handoverStepRoutes.risk}`}
          element={<Navigate to={`../${handoverStepRoutes.risknotes}`} replace />}
        />
        <Route
          path={`${handoverStepRoutes.risknotes}`}
          element={
            <FeatureBoundary
              features={[GlobalPermissionEnum.can_access_risk_notes]}
              showErrorIfDenied
            >
              <RiskNotesPage />
            </FeatureBoundary>
          }
        />
        <Route
          path={`${handoverStepRoutes.tasks}`}
          element={
            <FeatureBoundary
              features={[GlobalPermissionEnum.can_access_ehons_notes_goals]}
              showErrorIfDenied
            >
              <HandoverTaskPage />
            </FeatureBoundary>
          }
        />
        <Route
          path={`${handoverStepRoutes.summary}`}
          element={
            <FeatureBoundary
              features={[GlobalPermissionEnum.can_access_ehons_notes_goals]}
              showErrorIfDenied
            >
              <HandoverSummaryPage />
            </FeatureBoundary>
          }
        />
        {/* Adding just in case */}
        <Route
          path="/tasks-builder"
          element={
            <FeatureBoundary
              features={[GlobalPermissionEnum.can_access_ehons_notes_goals]}
              showErrorIfDenied
            >
              <CreateTask />
            </FeatureBoundary>
          }
        />
        <Route
          path="/tasks-builder/:taskId"
          element={
            <FeatureBoundary
              features={[GlobalPermissionEnum.can_access_ehons_notes_goals]}
              showErrorIfDenied
            >
              <CreateTask />
            </FeatureBoundary>
          }
        />
        <Route path="/leads-demo" element={<CreateRecommend isSppDA />} />
        <Route
          path="/goals-builder"
          element={
            <FeatureBoundary
              features={[GlobalPermissionEnum.can_access_ehons_notes_goals]}
              showErrorIfDenied
            >
              <CreateGoal />
            </FeatureBoundary>
          }
        />
        <Route
          path={`${handoverStepRoutes.report}`}
          element={
            <FeatureBoundary
              features={[GlobalPermissionEnum.can_access_report_pack]}
              showErrorIfDenied
            >
              <ReportPackPage />
            </FeatureBoundary>
          }
        />
      </Routes>
    </ReviewContextProvider>
  );
}

function AccountRoutes() {
  return (
    <AccountProvider>
      <Routes>
        <Route index element={<AccountDetails />} />
        <Route path="/:accountPageTabKey" element={<AccountDetails />} />
        <Route
          path="/precursor"
          element={
            <FeatureBoundary
              features={[GlobalPermissionEnum.can_access_ehons_notes_goals]}
              showErrorIfDenied
            >
              <PrePrecursorForm />
            </FeatureBoundary>
          }
        />
        <Route
          path="/tasks-builder/:taskId?"
          element={
            <FeatureBoundary
              features={[GlobalPermissionEnum.can_access_ehons_notes_goals]}
              showErrorIfDenied
            >
              <CreateTask />
            </FeatureBoundary>
          }
        />
        <Route
          path="/recommends-builder"
          element={
            <FeatureBoundary
              features={[GlobalPermissionEnum.can_access_instructions_FE]}
              showErrorIfDenied
            >
              <ReviewContextProvider>
                <CreateRecommend isSppDA />
              </ReviewContextProvider>
            </FeatureBoundary>
          }
        />
        <Route
          path="/builder"
          element={
            <FeatureBoundary
              features={[GlobalPermissionEnum.can_access_dentist_recommendation_builder]}
              showErrorIfDenied
            >
              <ReviewContextProvider>
                <CreateBuilder isSppDA />
              </ReviewContextProvider>
            </FeatureBoundary>
          }
        />
        <Route
          path="/review-journey/:reviewId/*"
          element={
            <FeatureBoundary
              features={[GlobalPermissionEnum.can_access_ehons_reqs]}
              showErrorIfDenied
            >
              <ReviewRoutes />
            </FeatureBoundary>
          }
        />
        <Route
          path="/goals/:reviewId"
          element={
            <FeatureBoundary
              features={[GlobalPermissionEnum.can_access_ehons_notes_goals]}
              showErrorIfDenied
            >
              <ClientGoalsPage />
            </FeatureBoundary>
          }
        />
        <Route
          path="/goals-builder"
          element={
            <FeatureBoundary
              features={[GlobalPermissionEnum.can_access_ehons_notes_goals]}
              showErrorIfDenied
            >
              <CreateGoal />
            </FeatureBoundary>
          }
        />
      </Routes>
    </AccountProvider>
  );
}

function AnnualReviewsRoutes() {
  return (
    <Routes>
      <Route path="/dashboard?/:reviewId?" element={<AnnualReviewsDashboardPage />} />
      <Route path="/invite/:reviewId?" element={<InvitePage />} />
      <Route path="/invited/:reviewId?" element={<InvitedPage />} />
      <Route path="/scheduled/:reviewId?" element={<ScheduledPage />} />
      <Route path="/completed/:reviewId?" element={<CompletedPage />} />
      <Route path="/declined/:reviewId?" element={<DeclinedPage />} />
      <Route path="/uncontactable/:reviewId?" element={<UncontactablePage />} />
      <Route path="/today/:reviewId?" element={<TodayPage />} />
    </Routes>
  );
}

const TestRoutes = () => {
  const envName = useMemo(() => {
    return getEnvironment();
  }, []);
  // Now showing for also prod
  return (
    <FeatureBoundary
      features={[GlobalPermissionEnum.can_access_admin_features]}
      showErrorIfDenied
    >
      <Routes>
        <Route path="/" element={<TestHomePage />} />
        <Route path="/set-group/:group" element={<TestSetGroupPage />} />
        <Route path="/regression" element={<QuickTestAccounts />} />
        <Route path="/test-account/:accountCuroId" element={<TestAccountPage />} />
        <Route path="/test-review/:reviewCuroId" element={<TestReviewPage />} />
        <Route
          path="/test-any-open-review/:accountCuroId"
          element={<TestAnyOpenReviewPage />}
        />
        <Route path="/test-instruction/:accountCuroId" element={<TestInstruction />} />
        <Route
          path="/test-new-review/:accountCuroId"
          element={<TestNewTestingReviewPage />}
        />
        <Route path="/*" element={<TestHomePage />} />
      </Routes>
    </FeatureBoundary>
  );
};

export function MainRoutes() {
  return (
    <>
      <Layout>
        <Routes>
          <Route path="/error" element={<ThrowErrorPage />} />
          <Route
            path="/test/*"
            element={
              <FeatureBoundary
                showErrorIfDenied
                features={[GlobalPermissionEnum.can_access_admin_features]}
              >
                <TestRoutes />
              </FeatureBoundary>
            }
          />
          <Route
            path={`/${ANNUAL_REVIEWS_NEW}/*`}
            element={
              <FeatureBoundary
                showErrorIfDenied
                features={[
                  GlobalPermissionEnum.can_access_annual_reviews,
                  GlobalPermissionEnum.can_access_admin_features,
                ]}
              >
                <AnnualReviewsRoutes />
              </FeatureBoundary>
            }
          />
          <Route
            path="/leads/basic"
            element={
              <FeatureBoundary
                showErrorIfDenied
                features={[GlobalPermissionEnum.can_access_lead_creator]}
              >
                <LeadFormContextProvider>
                  <LeadBasicForm formFields={[]} />
                </LeadFormContextProvider>
              </FeatureBoundary>
            }
          />
          <Route
            path="/leads/:leadId/*"
            element={
              <FeatureBoundary
                showErrorIfDenied
                features={[GlobalPermissionEnum.can_access_lead_creator]}
              >
                <LeadRoutes />
              </FeatureBoundary>
            }
          />
          <Route
            path="/add-account"
            element={
              <FeatureBoundary
                showErrorIfDenied
                features={[GlobalPermissionEnum.can_access_client_creator]}
              >
                <AddAccountRedirect />
              </FeatureBoundary>
            }
          />
          <Route
            path="/add-account/*"
            element={
              <FeatureBoundary
                showErrorIfDenied
                features={[GlobalPermissionEnum.can_access_client_creator]}
              >
                <AddAccountRoutes />
              </FeatureBoundary>
            }
          />
          <Route
            path="/replatforming"
            element={
              <FeatureBoundary
                showErrorIfDenied
                features={[GlobalPermissionEnum.can_access_client_creator]}
              >
                <AddAccountRedirectReplatforming />
              </FeatureBoundary>
            }
          />
          <Route
            path="/replatforming/*"
            element={
              <FeatureBoundary
                showErrorIfDenied
                features={[GlobalPermissionEnum.can_access_client_creator]}
              >
                <AddAccountRoutesReplatforming />
              </FeatureBoundary>
            }
          />
          <Route path="/contacts/:contactId" element={<ContactPage />} />
          <Route
            path="/"
            element={
              getEnvironment() === 'PROD' ? <Dashboard /> : <AccountContactSearchPage />
            }
          />
          <Route path="/home" element={<Dashboard />} />
          <Route path="/search" element={<AccountContactSearchPage />} />
          {/* <Route  path="/review/:reviewId" element={<ClientServiceReview />} /> */}
          <Route path="/reviews">
            <Route
              path=":stage"
              element={
                <FeatureBoundary
                  features={[GlobalPermissionEnum.can_access_ehons_notes_goals]}
                  showErrorIfDenied
                >
                  <ReviewsPage />
                </FeatureBoundary>
              }
            />
            <Route
              index
              element={
                <FeatureBoundary
                  features={[GlobalPermissionEnum.can_access_ehons_notes_goals]}
                  showErrorIfDenied
                >
                  <ReviewsPage />
                </FeatureBoundary>
              }
            />
          </Route>
          <Route path="/account/:accountId/*" element={<AccountRoutes />} />
          <Route
            path="/documents"
            element={
              <FeatureBoundary
                features={[GlobalPermissionEnum.can_access_documents]}
                showErrorIfDenied
              >
                <SPPDocumentsPage />
              </FeatureBoundary>
            }
          />
          <Route
            path="/bulk-admin"
            element={
              <FeatureBoundary
                showErrorIfDenied
                features={[GlobalPermissionEnum.can_access_bulk_accounts]}
              >
                <BulkAdminPage />
              </FeatureBoundary>
            }
          />
          <Route
            path="/report-pack-dashboard"
            element={
              <FeatureBoundary
                showErrorIfDenied
                features={[GlobalPermissionEnum.can_access_report_pack]}
              >
                <ReportPackDashboardPage />
              </FeatureBoundary>
            }
          />
          <Route
            path="/add-generic-document"
            element={
              <FeatureBoundary
                showErrorIfDenied
                features={[GlobalPermissionEnum.can_access_report_pack]}
              >
                <ManageDisclosuresPage />
              </FeatureBoundary>
            }
          />
          <Route
            path="/permissions-audit"
            element={
              <>
                <FeatureBoundary
                  showErrorIfDenied
                  features={[
                    GlobalPermissionEnum.can_access_admin_features,
                    GlobalPermissionEnum.can_restore_admin_permission,
                  ]}
                >
                  <RestoreButton />
                </FeatureBoundary>

                <FeatureBoundary
                  showErrorIfDenied
                  features={[GlobalPermissionEnum.can_access_admin_features]}
                >
                  <PermissionsAuditProvider>
                    <AdminPage formFields={[]} />
                  </PermissionsAuditProvider>
                </FeatureBoundary>
              </>
            }
          />
          <Route
            path="/identity"
            element={
              <>
                <FeatureBoundary
                  showErrorIfDenied
                  features={[GlobalPermissionEnum.can_access_admin_features]}
                >
                  <IdentityUserSearch />
                </FeatureBoundary>
              </>
            }
          />
          <Route
            path="/identity/:id"
            element={
              <FeatureBoundary
                showErrorIfDenied
                features={[GlobalPermissionEnum.can_access_admin_features]}
              >
                <SingleIdentityPage />
              </FeatureBoundary>
            }
          />
          <Route
            path="/staff"
            element={
              <FeatureBoundary
                showErrorIfDenied
                features={[GlobalPermissionEnum.can_access_admin_features]}
              >
                <StaffSearchPage />
              </FeatureBoundary>
            }
          />
          <Route
            path="/staff/:staffId"
            element={
              <FeatureBoundary
                showErrorIfDenied
                features={[GlobalPermissionEnum.can_access_admin_features]}
              >
                <StaffPage />
              </FeatureBoundary>
            }
          />
          <Route
            path="/audit"
            element={
              <FeatureBoundary
                showErrorIfDenied
                features={[GlobalPermissionEnum.can_access_admin_features]}
              >
                <AuditPage />
              </FeatureBoundary>
            }
          />
          <Route
            path="/soft-delete"
            element={
              <FeatureBoundary
                showErrorIfDenied
                features={[GlobalPermissionEnum.can_access_admin_features]}
              >
                <SoftDeletePage />
              </FeatureBoundary>
            }
          />
          <Route
            path="/manage-clients"
            element={
              <FeatureBoundary
                showErrorIfDenied
                features={[GlobalPermissionEnum.can_access_admin_features]}
              >
                <ManageClientsPageWrapped formFields={[]} />
              </FeatureBoundary>
            }
          />
          <Route
            path="/global-notifications"
            element={
              <FeatureBoundary
                showErrorIfDenied
                features={[GlobalPermissionEnum.can_access_admin_features]}
              >
                <ManageGlobalNotifications />
              </FeatureBoundary>
            }
          />
          <Route path="/email-audit" element={<EmailAuditPage />} />
          <Route path="/regression" element={<RegressionPage />} />
          <Route path="/review-redirect/:curoReviewId" element={<ReviewRedirect />} />
          <Route path="/account-redirect/:curoId" element={<AccountRedirect />} />
          <Route
            path="/account-redirect/:curoId/reviews"
            element={<AccountRedirect folder="reviews" />}
          />
          <Route
            path="/account-redirect/:curoId/requirements"
            element={<AccountRedirect folder="requirements" />}
          />
        </Routes>
      </Layout>
    </>
  );
}
